/**
 * Product Builder Page
 *
 * View products, see what they bundle, and check coverage.
 * Architect-only page for product catalog management and coverage analysis.
 *
 * Pattern: follows RegistryExplorerPage (stats cards + grid + detail)
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  SimpleGrid,
  Loader,
  List,
  Button,
  Collapse,
  Code,
  Tabs,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconPackage,
  IconPlayerPlay,
  IconFileText,
  IconShieldCheck,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react';
import {
  useListProductDefinitionsQuery,
  useGetCoverageGapsForProductLazyQuery,
} from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

interface Product {
  productId: string;
  name: string;
  description?: string | null;
  status: string;
  industry: string;
  jurisdiction: string;
  activatesWorkflows: string[];
  activatesTaskGroups: string[];
  activatesOverrides: string[];
  requiredRulesets: string[];
}

interface CoverageResult {
  rulesetId: string;
  totalRules: number;
  coveredRules: number;
  coveragePercent: number;
  gapCount: number;
}

// ============================================================================
// Components
// ============================================================================

function StatsCards({ products }: { products: Product[] }) {
  const statusCounts = products.reduce<Record<string, number>>((acc, p) => {
    const key = p.status.toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const active = Object.entries(statusCounts)
    .filter(([k]) => k.includes('activ'))
    .reduce((sum, [, v]) => sum + v, 0);
  const draft = products.length - active;

  return (
    <SimpleGrid cols={{ base: 2, sm: 3 }} mb="lg">
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue"><IconPackage size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Total Products</Text>
        </Group>
        <Text size="lg" fw={700}>{products.length}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green"><IconPlayerPlay size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Active</Text>
        </Group>
        <Text size="lg" fw={700}>{active}</Text>
      </Card>
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="yellow"><IconFileText size={14} /></ThemeIcon>
          <Text size="xs" c="dimmed">Draft</Text>
        </Group>
        <Text size="lg" fw={700}>{draft}</Text>
      </Card>
    </SimpleGrid>
  );
}

function ProductCard({
  product,
  onCoverageCheck,
  coverageResults,
  coverageLoading,
}: {
  product: Product;
  onCoverageCheck: (rulesetIds: string[]) => void;
  coverageResults: CoverageResult[];
  coverageLoading: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const STATUS_COLORS: Record<string, string> = {};
  const statusColor = STATUS_COLORS[product.status.toLowerCase()] ?? 'gray';

  return (
    <Card withBorder p="md">
      <Group justify="space-between" mb="xs">
        <Text size="sm" fw={600}>{product.name}</Text>
        <Badge color={statusColor} variant="light" size="sm">{product.status}</Badge>
      </Group>

      <Text size="xs" c="dimmed" mb="sm" lineClamp={2}>{product.description}</Text>

      <Group gap="xs" mb="sm">
        <Badge variant="outline" size="xs">{product.industry}</Badge>
        <Badge variant="outline" size="xs">{product.jurisdiction}</Badge>
      </Group>

      <Group gap="xs" mb="sm">
        <Button
          size="xs"
          variant="light"
          onClick={() => setExpanded(!expanded)}
          rightSection={expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
        >
          {expanded ? 'Collapse' : 'Details'}
        </Button>
        {product.requiredRulesets.length > 0 && (
          <Button
            size="xs"
            variant="light"
            color="teal"
            leftSection={<IconShieldCheck size={14} />}
            onClick={() => onCoverageCheck(product.requiredRulesets)}
            loading={coverageLoading}
          >
            Coverage Check
          </Button>
        )}
      </Group>

      <Collapse in={expanded}>
        <Stack gap="sm" mt="sm">
          <Tabs defaultValue="workflows" variant="outline">
            <Tabs.List>
              <Tabs.Tab value="workflows">Workflows ({product.activatesWorkflows.length})</Tabs.Tab>
              <Tabs.Tab value="taskgroups">Task Groups ({product.activatesTaskGroups.length})</Tabs.Tab>
              <Tabs.Tab value="overrides">Overrides ({product.activatesOverrides.length})</Tabs.Tab>
              <Tabs.Tab value="rulesets">Rulesets ({product.requiredRulesets.length})</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="workflows" pt="xs">
              {product.activatesWorkflows.length === 0 ? (
                <Text size="xs" c="dimmed">No workflows</Text>
              ) : (
                <List size="sm">
                  {product.activatesWorkflows.map((wf) => (
                    <List.Item key={wf}><Code>{wf}</Code></List.Item>
                  ))}
                </List>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="taskgroups" pt="xs">
              {product.activatesTaskGroups.length === 0 ? (
                <Text size="xs" c="dimmed">No task groups</Text>
              ) : (
                <List size="sm">
                  {product.activatesTaskGroups.map((tg) => (
                    <List.Item key={tg}><Code>{tg}</Code></List.Item>
                  ))}
                </List>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="overrides" pt="xs">
              {product.activatesOverrides.length === 0 ? (
                <Text size="xs" c="dimmed">No overrides</Text>
              ) : (
                <List size="sm">
                  {product.activatesOverrides.map((ov) => (
                    <List.Item key={ov}><Code>{ov}</Code></List.Item>
                  ))}
                </List>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="rulesets" pt="xs">
              {product.requiredRulesets.length === 0 ? (
                <Text size="xs" c="dimmed">No rulesets</Text>
              ) : (
                <List size="sm">
                  {product.requiredRulesets.map((rs) => (
                    <List.Item key={rs}><Code>{rs}</Code></List.Item>
                  ))}
                </List>
              )}
            </Tabs.Panel>
          </Tabs>

          {coverageResults.length > 0 && (
            <Card withBorder p="sm" bg="gray.0">
              <Text size="xs" fw={600} mb="xs">
                <IconShieldCheck size={14} style={{ verticalAlign: 'middle' }} /> Coverage Results
              </Text>
              {coverageResults.map((cr) => {
                const color = cr.coveragePercent >= 80 ? 'green' : cr.coveragePercent >= 50 ? 'yellow' : 'red';
                return (
                  <Group key={cr.rulesetId} gap="xs" mb="xs">
                    <Code>{cr.rulesetId}</Code>
                    <Badge color={color} variant="light" size="sm">
                      {cr.coveragePercent.toFixed(1)}%
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {cr.coveredRules}/{cr.totalRules} rules, {cr.gapCount} gaps
                    </Text>
                  </Group>
                );
              })}
            </Card>
          )}
        </Stack>
      </Collapse>
    </Card>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function ProductBuilderPage() {
  const { data, loading, error } = useListProductDefinitionsQuery();
  const [coverageResults, setCoverageResults] = useState<Record<string, CoverageResult[]>>({});
  const [coverageLoadingProduct, setCoverageLoadingProduct] = useState<string | null>(null);

  const [fetchCoverage] = useGetCoverageGapsForProductLazyQuery();

  const products: Product[] = (data?.productDefinitions ?? []).map((p) => ({
    productId: p.productId,
    name: p.name,
    description: p.description,
    status: p.status,
    industry: p.industry,
    jurisdiction: p.jurisdiction,
    activatesWorkflows: p.activatesWorkflows,
    activatesTaskGroups: p.activatesTaskGroups,
    activatesOverrides: p.activatesOverrides,
    requiredRulesets: p.requiredRulesets,
  }));

  const handleCoverageCheck = async (productId: string, rulesetIds: string[]) => {
    setCoverageLoadingProduct(productId);
    const results: CoverageResult[] = [];

    for (const rsId of rulesetIds) {
      const { data: gapData } = await fetchCoverage({ variables: { rulesetId: rsId } });
      if (gapData?.regulatoryCoverageGaps) {
        const g = gapData.regulatoryCoverageGaps;
        results.push({
          rulesetId: g.rulesetId,
          totalRules: g.totalRules,
          coveredRules: g.coveredRules,
          coveragePercent: g.coveragePercent,
          gapCount: g.gaps.length,
        });
      }
    }

    setCoverageResults((prev) => ({ ...prev, [productId]: results }));
    setCoverageLoadingProduct(null);
  };

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Products">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Loading products...</Text>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Product Builder</Title>
          <Text size="sm" c="dimmed">
            View products, bundled configs, and regulatory coverage
          </Text>
        </div>
        <Badge variant="light" color="violet" size="lg">Architect</Badge>
      </Group>

      <StatsCards products={products} />

      {products.length === 0 ? (
        <Alert color="blue" variant="light" icon={<IconPackage size={16} />}>
          No products found. Products are defined in system configuration under the product catalog.
        </Alert>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {products.map((product) => (
            <ProductCard
              key={product.productId}
              product={product}
              onCoverageCheck={(rulesetIds) => handleCoverageCheck(product.productId, rulesetIds)}
              coverageResults={coverageResults[product.productId] ?? []}
              coverageLoading={coverageLoadingProduct === product.productId}
            />
          ))}
        </SimpleGrid>
      )}
    </Container>
  );
}
